/**
 * Flag colour palettes for directory countries.
 *
 * Used to paint the country ribbon on a company profile — the stripe reads as
 * "this company flies this flag" without shipping flag imagery.
 * Unknown countries fall back to the Trados brand gradient.
 */

const BRAND_FALLBACK = ['#2B4B6A', '#F58A5A', '#FFD166'];

// Canonical lowercase country name -> flag colours, in flag order.
const PALETTES = {
  'united states': ['#B22234', '#FFFFFF', '#3C3B6E'],
  brazil: ['#009C3B', '#FFDF00', '#002776'],
  china: ['#DE2910', '#FFDE00'],
  india: ['#FF9933', '#FFFFFF', '#138808'],
  netherlands: ['#AE1C28', '#FFFFFF', '#21468B'],
  germany: ['#000000', '#DD0000', '#FFCE00'],
  france: ['#0055A4', '#FFFFFF', '#EF4135'],
  'united kingdom': ['#012169', '#FFFFFF', '#C8102E'],
  spain: ['#AA151B', '#F1BF00', '#AA151B'],
  italy: ['#008C45', '#F4F5F0', '#CD212A'],
  portugal: ['#046A38', '#DA291C', '#FFE900'],
  mexico: ['#006847', '#FFFFFF', '#CE1126'],
  argentina: ['#74ACDF', '#FFFFFF', '#F6B40E'],
  chile: ['#0039A6', '#FFFFFF', '#D52B1E'],
  colombia: ['#FCD116', '#003893', '#CE1126'],
  peru: ['#D91023', '#FFFFFF', '#D91023'],
  canada: ['#FF0000', '#FFFFFF', '#FF0000'],
  australia: ['#00008B', '#FFFFFF', '#FF0000'],
  japan: ['#FFFFFF', '#BC002D'],
  'south korea': ['#FFFFFF', '#CD2E3A', '#0047A0'],
  thailand: ['#A51931', '#F4F5F8', '#2D2A4A'],
  vietnam: ['#DA251D', '#FFFF00'],
  indonesia: ['#FF0000', '#FFFFFF'],
  malaysia: ['#CC0001', '#FFFFFF', '#010066'],
  singapore: ['#EF3340', '#FFFFFF'],
  philippines: ['#0038A8', '#FFFFFF', '#CE1126'],
  pakistan: ['#01411C', '#FFFFFF'],
  bangladesh: ['#006A4E', '#F42A41'],
  turkey: ['#E30A17', '#FFFFFF'],
  'united arab emirates': ['#00732F', '#FFFFFF', '#000000', '#FF0000'],
  'saudi arabia': ['#006C35', '#FFFFFF'],
  qatar: ['#8A1538', '#FFFFFF'],
  kuwait: ['#007A3D', '#FFFFFF', '#CE1126'],
  oman: ['#DB161B', '#FFFFFF', '#008000'],
  egypt: ['#CE1126', '#FFFFFF', '#000000'],
  'south africa': ['#007A4D', '#FFB612', '#DE3831', '#002395'],
  nigeria: ['#008751', '#FFFFFF'],
  kenya: ['#006600', '#FFFFFF', '#BB0000'],
  ghana: ['#CE1126', '#FCD116', '#006B3F'],
  ethiopia: ['#078930', '#FCDD09', '#DA121A'],
  morocco: ['#C1272D', '#006233'],
  algeria: ['#006233', '#FFFFFF', '#D21034'],
  tunisia: ['#E70013', '#FFFFFF'],
  ukraine: ['#0057B7', '#FFD700'],
  russia: ['#FFFFFF', '#0039A6', '#D52B1E'],
  poland: ['#FFFFFF', '#DC143C'],
  belgium: ['#000000', '#FDDA24', '#EF3340'],
  switzerland: ['#FF0000', '#FFFFFF'],
  austria: ['#ED2939', '#FFFFFF'],
  sweden: ['#006AA7', '#FECC00'],
  norway: ['#BA0C2F', '#FFFFFF', '#00205B'],
  denmark: ['#C60C30', '#FFFFFF'],
  finland: ['#FFFFFF', '#003580'],
  ireland: ['#169B62', '#FFFFFF', '#FF883E'],
  greece: ['#0D5EAF', '#FFFFFF'],
  romania: ['#002B7F', '#FCD116', '#CE1126'],
  hungary: ['#CD2A3E', '#FFFFFF', '#436F4D'],
  czechia: ['#11457E', '#FFFFFF', '#D7141A'],
  'new zealand': ['#00247D', '#FFFFFF', '#CC142B'],
  israel: ['#FFFFFF', '#0038B8'],
  paraguay: ['#D52B1E', '#FFFFFF', '#0038A8'],
  uruguay: ['#0038A8', '#FFFFFF'],
  bolivia: ['#D52B1E', '#F9E300', '#007934'],
  ecuador: ['#FFDD00', '#0033A0', '#EF3340'],
  venezuela: ['#FFCC00', '#00247D', '#CF142B'],
  guatemala: ['#4997D0', '#FFFFFF'],
  'costa rica': ['#002B7F', '#FFFFFF', '#CE1126'],
  panama: ['#005293', '#FFFFFF', '#D21034'],
  'dominican republic': ['#002D62', '#FFFFFF', '#CE1126'],
  honduras: ['#0073CF', '#FFFFFF'],
  nicaragua: ['#0067C6', '#FFFFFF'],
  'sri lanka': ['#8D2029', '#FFB700', '#00534E'],
  myanmar: ['#FECB00', '#34B233', '#EA2839'],
  cambodia: ['#032EA1', '#E00025', '#FFFFFF'],
  taiwan: ['#000095', '#FFFFFF', '#FE0000'],
  'hong kong': ['#DE2910', '#FFFFFF'],
};

// Directory strings that don't match the canonical keys above.
const ALIASES = {
  usa: 'united states',
  us: 'united states',
  'u.s.a.': 'united states',
  'united states of america': 'united states',
  uk: 'united kingdom',
  'great britain': 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  korea: 'south korea',
  'republic of korea': 'south korea',
  'czech republic': 'czechia',
  holland: 'netherlands',
};

/** Flag colours for a country string; brand colours when unknown. */
export const countryPalette = (country) => {
  if (!country) return BRAND_FALLBACK;
  const key = String(country).trim().toLowerCase();
  return PALETTES[ALIASES[key] || key] || BRAND_FALLBACK;
};

/** Hard-edged flag-stripe gradient for the ribbon.
 *  White stripes stay true white — the ribbon sits on the card's light green
 *  footer band, so they read as part of the flag rather than a gap. */
export const countryRibbonGradient = (country) => {
  const colors = countryPalette(country);
  const step = 100 / colors.length;
  const stops = colors
    .map((c, i) => `${c} ${i * step}%, ${c} ${(i + 1) * step}%`)
    .join(', ');
  return `linear-gradient(90deg, ${stops})`;
};

export default countryPalette;
